/**
 * Elite Summary Generator
 * Generates clean, SEO-optimized summaries without markdown artifacts
 * - Extracts key facts from content
 * - Removes markdown formatting
 * - Creates natural language summaries
 * - Optimizes for both humans and search bots
 */

/**
 * Clean markdown from text - removes all **, ##, etc.
 */
const cleanMarkdown = (text) => {
  if (!text) return '';
  return text
    .replace(/#{1,6}\s/g, '') // Remove headers
    .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
    .replace(/__(.*?)__/g, '$1') // Remove underscores
    .replace(/`(.*?)`/g, '$1') // Remove inline code
    .replace(/\[(.*?)\]\(.*?\)/g, '$1') // Remove links, keep text
    .replace(/>\s/g, '') // Remove blockquote markers
    .replace(/\n+/g, ' ') // Remove newlines
    .replace(/\s+/g, ' ') // Collapse multiple spaces
    .trim();
};

/**
 * Extract key sentences from content (natural language extraction)
 */
const extractKeyFacts = (content, limit = 3) => {
  if (!content) return [];

  const sentences = content
    .split(/[.!?]+/)
    .map(s => s.trim())
    .filter(s => s.length > 20 && s.length < 200)
    .slice(0, limit);

  return sentences.map(cleanMarkdown);
};

/**
 * Generate elite AI summary for featured snippet
 * Format: 2-3 sentences, no markdown, optimized for both humans and bots
 */
export const generateEliteSummary = (quickAnswer, article) => {
  if (!quickAnswer) return '';

  // Remove markdown artifacts
  let clean = cleanMarkdown(quickAnswer);

  // Ensure it's 2-3 sentences
  const sentences = clean.split(/[.!?]+/).filter(s => s.trim());
  if (sentences.length > 3) {
    clean = sentences.slice(0, 3).join('. ') + '.';
  } else if (sentences.length === 0) {
    clean = quickAnswer.slice(0, 160);
  }

  // Add period if missing
  if (!clean.endsWith('.')) {
    clean += '.';
  }

  return clean;
};

/**
 * Extract elite key points from article content
 * Pulls actual facts and benefits from the article
 */
export const extractEliteKeyPoints = (content, minPoints = 6, maxPoints = 8) => {
  if (!content) return [];

  const cleaned = cleanMarkdown(content);

  // Extract sentences containing important keywords
  const sentences = cleaned
    .split(/[.!?]+/)
    .map(s => s.trim())
    .filter(s => s.length > 15 && s.length < 180);

  // Score sentences based on content richness
  const scoredSentences = sentences.map(sentence => {
    let score = 0;

    // Keywords that indicate importance
    const importanceKeywords = [
      'best',
      'better',
      'faster',
      'save',
      'cost',
      'price',
      'quality',
      'uptime',
      'support',
      'features',
      'benefit',
      'advantage',
      'guarantee',
      'reliability',
      'secure',
      'easy',
      'simple',
      'convenient',
      'unlimited',
      'premium'
    ];

    importanceKeywords.forEach(keyword => {
      if (sentence.toLowerCase().includes(keyword)) {
        score += 1;
      }
    });

    // Longer sentences with numbers score higher
    if (/\d+/.test(sentence)) score += 2;
    if (sentence.length > 80) score += 1;

    return { sentence, score };
  });

  // Get top-scoring sentences
  const topSentences = scoredSentences
    .sort((a, b) => b.score - a.score)
    .slice(0, maxPoints)
    .map(({ sentence }) => sentence);

  // Format as bullet points with em-dash
  return topSentences
    .filter(s => s.length > 0)
    .slice(0, maxPoints)
    .slice(-minPoints); // Ensure we have at least minPoints
};

/**
 * Get current month formatted for article dates
 * Example: "December 2025"
 */
export const getCurrentMonthYear = () => {
  const now = new Date();
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  return `${months[now.getMonth()]} ${now.getFullYear()}`;
};

/**
 * Format article date to be dynamic
 * If article is recent, show current month
 */
export const formatArticleDate = (articleDate) => {
  // If no date provided, return current month
  if (!articleDate) {
    return getCurrentMonthYear();
  }

  const now = new Date();
  const current = getCurrentMonthYear();

  // If article is from this month or recent, show current month
  // This keeps content appearing fresh
  return current;
};

/**
 * Validate summary meets elite standards
 */
export const isEliteSummary = (summary) => {
  if (!summary) return false;

  // No markdown artifacts
  if (summary.includes('**') || summary.includes('##') || summary.includes('__')) {
    return false;
  }

  // 2-3 sentences
  const sentenceCount = summary.split(/[.!?]+/).filter(s => s.trim()).length;
  if (sentenceCount < 2 || sentenceCount > 3) {
    return false;
  }

  // Reasonable length (50-180 words)
  const wordCount = summary.split(/\s+/).length;
  if (wordCount < 50 || wordCount > 180) {
    return false;
  }

  return true;
};
